import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, Edit3, Mail, CreditCard, MapPin, Sparkles, Loader2, X } from "lucide-react";

const CATEGORIES = [
  { value: "international", label: "International" },
  { value: "domestic", label: "Domestic" },
  { value: "fixed-departures", label: "Fixed Departures" },
  { value: "short-trip", label: "Short First Day Trip" },
  { value: "adventure", label: "Adventure" },
  { value: "devotional", label: "Devotional Tour" },
];

const emptyForm = {
  name: "", category: "international", country: "", city: "", short_description: "",
  description: "", price: 0, duration_days: 1, duration_nights: 0, image_url: "",
  latitude: null, longitude: null, featured: false,
  highlights: "", inclusions: "", exclusions: "", itinerary: "", gallery: [],
};

function makeDayRows(count, existing = []) {
  const total = Math.max(1, Number(count) || 1);
  return Array.from({ length: total }, (_, index) => ({
    day: index + 1,
    title: existing[index]?.title || "",
    description: existing[index]?.description || "",
  }));
}

function serializeForm(f, itineraryDays = []) {
  const parseList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const rows = itineraryDays.length
    ? itineraryDays
    : f.itinerary.split("\n").map((line, i) => {
        const [title, ...rest] = line.split("|");
        return { day: i + 1, title: title?.trim() || "", description: (rest.join("|") || "").trim() };
      });
  const itin = rows
    .map((item, i) => ({ day: i + 1, title: item.title.trim(), description: item.description.trim() }))
    .filter((item) => item.title || item.description);
  const gallery = Array.from(new Set([f.image_url, ...(f.gallery || [])].filter(Boolean)));
  return {
    ...f,
    price: Number(f.price) || 0,
    duration_days: Number(f.duration_days) || 1,
    duration_nights: Number(f.duration_nights) || 0,
    latitude: f.latitude === "" ? null : (f.latitude != null ? Number(f.latitude) : null),
    longitude: f.longitude === "" ? null : (f.longitude != null ? Number(f.longitude) : null),
    highlights: parseList(f.highlights),
    inclusions: parseList(f.inclusions),
    exclusions: parseList(f.exclusions),
    itinerary: itin,
    image_url: gallery[0] || "",
    gallery,
  };
}

function deserializeForm(d) {
  const gallery = Array.from(new Set([d.image_url, ...(d.gallery || [])].filter(Boolean)));
  return {
    ...emptyForm, ...d,
    image_url: gallery[0] || "",
    gallery,
    highlights: (d.highlights || []).join("\n"),
    inclusions: (d.inclusions || []).join("\n"),
    exclusions: (d.exclusions || []).join("\n"),
    itinerary: (d.itinerary || []).map((it) => `${it.title} | ${it.description}`).join("\n"),
  };
}

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const [dests, setDests] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [itineraryDays, setItineraryDays] = useState(makeDayRows(1));
  const [aiImages, setAiImages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const addGalleryUrl = () => {
    const url = window.prompt("Paste the photo URL");
    if (!url?.trim()) return;
    const next = Array.from(new Set([...(form.gallery || []), url.trim()]));
    setForm({ ...form, gallery: next, image_url: form.image_url || url.trim() });
  };

  const removeGalleryUrl = (index) => {
    const next = (form.gallery || []).filter((_, i) => i !== index);
    setForm({ ...form, gallery: next, image_url: next[0] || "" });
  };

  const selectPrimary = (url) => {
    const next = [url, ...(form.gallery || []).filter((item) => item !== url)];
    setForm({ ...form, gallery: next, image_url: url });
  };

  const generateAiImages = async () => {
    if (!form.name) { toast.error("Enter a destination name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/generate-images", {
        name: form.name,
        city: form.city,
        country: form.country,
        category: form.category,
        description: form.description,
        highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
        count: 3,
      });
      setAiImages(data.images || []);
      toast.success(`Generated ${data.images?.length || 0} images — pick one below`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image generation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const addAiImageToGallery = (img) => {
    const url = img.data_url;
    const next = Array.from(new Set([...(form.gallery || []), url]));
    setForm({ ...form, gallery: next, image_url: form.image_url || url });
  };

  const refresh = async () => {
    const [d, i, b] = await Promise.all([
      api.get("/destinations"),
      api.get("/inquiries").catch(() => ({ data: [] })),
      api.get("/admin/bookings").catch(() => ({ data: [] })),
    ]);
    setDests(d.data); setInquiries(i.data); setBookings(b.data);
  };

  useEffect(() => { if (user?.is_admin) refresh(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <div className="max-w-md mx-auto px-6 py-24 text-center text-muted-foreground">Admin access only.</div>;

  const openNew = () => {
    const nextForm = { ...emptyForm };
    setEditing(null);
    setForm(nextForm);
    setItineraryDays(makeDayRows(nextForm.duration_days));
    setAiImages([]);
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    const nextForm = deserializeForm(d);
    const existingDays = (d.itinerary || []).map((it) => ({ title: it.title || "", description: it.description || "" }));
    setEditing(d);
    setForm(nextForm);
    setItineraryDays(makeDayRows(nextForm.duration_days, existingDays));
    setAiImages([]);
    setDialogOpen(true);
  };

  const changeDurationDays = (value) => {
    const days = Math.max(1, Number(value) || 1);
    setForm({ ...form, duration_days: value });
    setItineraryDays((current) => makeDayRows(days, current));
  };

  const updateItineraryDay = (index, field, value) => {
    setItineraryDays((current) => current.map((day, i) => i === index ? { ...day, [field]: value } : day));
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = serializeForm(form, itineraryDays);
    try {
      if (editing) await api.put(`/destinations/${editing.id}`, payload);
      else await api.post("/destinations", payload);
      toast.success("Saved!");
      setDialogOpen(false); refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/destinations/${d.id}`);
    toast.success("Deleted");
    refresh();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">Admin C-Panel</div>
      <h1 className="heading-serif text-5xl mb-8">Manage OITS</h1>

      <Tabs defaultValue="destinations" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="destinations" data-testid="tab-destinations"><MapPin className="mr-2 h-4 w-4" /> Destinations</TabsTrigger>
          <TabsTrigger value="inquiries" data-testid="tab-inquiries"><Mail className="mr-2 h-4 w-4" /> Inquiries</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings"><CreditCard className="mr-2 h-4 w-4" /> Bookings</TabsTrigger>
        </TabsList>

        <TabsContent value="destinations">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-muted-foreground">{dests.length} destinations</div>
            <Button onClick={openNew} className="rounded-full bg-accent text-accent-foreground" data-testid="new-destination-btn">
              <Plus className="h-4 w-4 mr-2" /> New destination
            </Button>
          </div>
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr>
                <th className="p-3">Name</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Featured</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {dests.map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="p-3 font-medium">{d.name}</td>
                    <td className="p-3">{d.category}</td>
                    <td className="p-3">₹{Number(d.price).toLocaleString("en-IN")}</td>
                    <td className="p-3">{d.featured ? "✓" : ""}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`edit-${d.slug}`}><Edit3 className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => del(d)} data-testid={`del-${d.slug}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="inquiries">
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr>
                <th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">Destination</th><th className="p-3">Message</th><th className="p-3">When</th>
              </tr></thead>
              <tbody>
                {inquiries.map((i) => (
                  <tr key={i.inquiry_id} className="border-t border-border">
                    <td className="p-3 font-medium">{i.name}</td>
                    <td className="p-3"><div>{i.email}</div><div className="text-xs text-muted-foreground">{i.phone}</div></td>
                    <td className="p-3">{i.destination_name || "General"}</td>
                    <td className="p-3 max-w-md truncate">{i.message}</td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {inquiries.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No inquiries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left"><tr>
                <th className="p-3">Destination</th><th className="p-3">Customer</th><th className="p-3">Phone</th><th className="p-3">Travelers</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Date</th>
              </tr></thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.session_id} className="border-t border-border">
                    <td className="p-3">{b.destination_name}</td>
                    <td className="p-3 font-medium">{b.customer_name || "—"}</td>
                    <td className="p-3">{b.customer_phone || "—"}</td>
                    <td className="p-3">{b.travelers}</td>
                    <td className="p-3">₹{Number(b.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="p-3">{b.payment_status}</td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit destination" : "New destination"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="destination-form">
            <div className="col-span-2"><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Price (INR)</Label><Input required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" /></div>
            <div><Label>Days</Label><Input type="number" min="1" value={form.duration_days} onChange={(e) => changeDurationDays(e.target.value)} /></div>
            <div><Label>Nights</Label><Input type="number" min="0" value={form.duration_nights} onChange={(e) => setForm({ ...form, duration_nights: e.target.value })} /></div>
            <div className="col-span-2"><Label>Image URL</Label><Input required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="form-image" /></div>
            {(form.gallery || []).length > 0 && (
              <div className="col-span-2 border border-border rounded-lg p-4 bg-secondary/20">
                <div className="text-sm font-medium mb-3">Itinerary photos ({form.gallery.length})</div>
                <div className="grid grid-cols-3 gap-3">
                  {form.gallery.map((url, i) => (
                    <div key={`${url}-${i}`} className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 ${i === 0 ? "border-accent" : "border-border"}`}>
                      <img src={url} alt={`${form.name} ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute bottom-1 left-1 right-1 flex gap-1">
                        <Button type="button" size="sm" onClick={() => selectPrimary(url)} className="flex-1 h-7 text-[10px] rounded bg-black/70 text-white hover:bg-black/80">{i === 0 ? "Main photo" : "Make main"}</Button>
                        <Button type="button" size="icon" onClick={() => removeGalleryUrl(i)} className="h-7 w-7 rounded bg-black/70 text-white hover:bg-black/80"><X className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" onClick={addGalleryUrl} className="mt-3 rounded-full"><Plus className="h-4 w-4 mr-2" /> Add another photo URL</Button>
              </div>
            )}
            {!form.gallery?.length && <div className="col-span-2"><Button type="button" variant="outline" onClick={addGalleryUrl} className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Add more itinerary photos</Button></div>}
            <div className="col-span-2 border border-dashed border-border rounded-lg p-4 bg-secondary/30">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /> AI photo suggestions</div>
                  <div className="text-xs text-muted-foreground">Generate destination photos with AI based on the itinerary details above.</div>
                </div>
                <Button type="button" onClick={generateAiImages} disabled={aiLoading} className="rounded-full bg-accent text-accent-foreground" data-testid="ai-generate-btn">
                  {aiLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4 mr-2" /> {aiImages.length ? "Regenerate" : "Generate images"}</>}
                </Button>
              </div>
              {aiImages.length > 0 && (
                <div className="grid grid-cols-3 gap-3" data-testid="ai-images-grid">
                  {aiImages.map((img, i) => {
                    const selected = (form.gallery || []).includes(img.data_url);
                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => addAiImageToGallery(img)}
                        data-testid={`ai-img-${i}`}
                        className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition ${selected ? "border-accent ring-2 ring-accent/40" : "border-border hover:border-accent/60"}`}
                      >
                        <img src={img.data_url} alt={`Option ${i + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute bottom-1 left-1 right-1 text-[10px] uppercase tracking-widest bg-black/60 text-white rounded px-1 py-0.5 text-center">
                          {selected ? "Added to gallery" : "Add to gallery"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="col-span-2"><Label>Short description</Label><Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2"><Label>Highlights (one per line)</Label><Textarea rows={3} value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} /></div>
            <div><Label>Inclusions (one per line)</Label><Textarea rows={3} value={form.inclusions} onChange={(e) => setForm({ ...form, inclusions: e.target.value })} /></div>
            <div><Label>Exclusions (one per line)</Label><Textarea rows={3} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} /></div>

            <div className="col-span-2">
              <div className="flex items-end justify-between gap-4 mb-3">
                <div>
                  <Label>Day-by-day itinerary</Label>
                  <div className="text-xs text-muted-foreground mt-1">Enter the number of days above and a separate section will appear for every day.</div>
                </div>
                <div className="text-xs text-muted-foreground whitespace-nowrap">{itineraryDays.length} day{itineraryDays.length === 1 ? "" : "s"}</div>
              </div>
              <div className="space-y-3">
                {itineraryDays.map((day, index) => (
                  <div key={day.day} className="rounded-xl border border-border bg-secondary/20 p-4 transition-colors hover:border-accent/50">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground text-sm font-semibold">{day.day}</span>
                      <div>
                        <div className="heading-serif text-xl">Day {day.day}</div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Day {day.day} details</div>
                      </div>
                    </div>
                    <Input
                      placeholder={`Day ${day.day} title`}
                      value={day.title}
                      onChange={(e) => updateItineraryDay(index, "title", e.target.value)}
                      data-testid={`itinerary-day-${day.day}-title`}
                    />
                    <Textarea
                      rows={3}
                      className="mt-2"
                      placeholder={`What happens on Day ${day.day}?`}
                      value={day.description}
                      onChange={(e) => updateItineraryDay(index, "description", e.target.value)}
                      data-testid={`itinerary-day-${day.day}-description`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-2 flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><Label>Featured on homepage</Label></div>
            <DialogFooter className="col-span-2"><Button type="submit" className="rounded-full bg-accent text-accent-foreground" data-testid="form-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
